import React, { useState } from "react";
import axios from "axios";
import { ADMIN } from "../constants/testIds";
import { humanizeKey } from "../lib/scoring";
import AdminReport from "./AdminReport";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPage() {
  const [passcode, setPasscode] = useState("");
  const [authed, setAuthed] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [selected, setSelected] = useState(null);

  const fetchSubmissions = async (code) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/submissions`, {
        headers: { "X-Admin-Passcode": code },
      });
      setSubmissions(res.data || []);
      setAuthed(true);
    } catch (err) {
      if (err?.response?.status === 401) setError("Incorrect passcode.");
      else setError("Could not load profiles. Please try again.");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!passcode.trim()) return;
    fetchSubmissions(passcode.trim());
  };

  const handleRefresh = () => {
    // SEC-20: the passcode lives in React state only (never persisted to
    // sessionStorage), so it isn't readable via devtools or replayed on
    // reload. Reuse the value the operator typed at the gate.
    const code = passcode.trim();
    if (code) fetchSubmissions(code);
  };

  // ---- Passcode gate ----
  if (!authed) {
    return (
      <div className="gw-page gw-grain">
        <div className="gw-card gw-fade-up" data-testid={ADMIN.page} style={{ maxWidth: 420 }}>
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <span className="gw-small" style={{ color: "var(--text-secondary)" }}>
                Internal access
              </span>
              <h1 className="gw-headline">Operations Intelligence Profile</h1>
              <p className="gw-body" style={{ color: "var(--text-secondary)" }}>
                Enter the admin passcode to view setup profiles.
              </p>
            </div>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <input
                type="password"
                className="gw-input"
                placeholder="Passcode"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                data-testid={ADMIN.passcodeInput}
                autoFocus
              />
              {error && (
                <span className="gw-small" style={{ color: "var(--error)" }} data-testid={ADMIN.passcodeError}>
                  {error}
                </span>
              )}
              <button
                type="submit"
                className="gw-btn-primary w-full"
                disabled={loading || !passcode.trim()}
                data-testid={ADMIN.passcodeSubmit}
              >
                {loading ? "Checking…" : "View profiles"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ---- Selected report ----
  if (selected) {
    return (
      <div className="gw-page gw-grain">
        <div className="gw-card gw-card-wide gw-fade-up" data-testid={ADMIN.report}>
          <div className="gw-card-header flex items-center justify-between" style={{ marginBottom: 16 }}>
            <button
              type="button"
              className="gw-btn-secondary"
              onClick={() => setSelected(null)}
              data-testid={ADMIN.backToListBtn}
            >
              ← All profiles
            </button>
            <span className="gw-caption" style={{ color: "var(--text-secondary)" }}>
              Operations Intelligence Profile
            </span>
          </div>
          <div className="gw-card-body">
            <AdminReport data={selected} />
          </div>
        </div>
      </div>
    );
  }

  // ---- List ----
  return (
    <div className="gw-page gw-grain">
      <div className="gw-card gw-card-wide gw-fade-up" data-testid={ADMIN.submissionsList}>
        <div className="gw-card-header flex items-center justify-between" style={{ marginBottom: 16 }}>
          <div className="flex flex-col gap-1">
            <h1 className="gw-headline" style={{ fontSize: 24 }}>Setup profiles</h1>
            <span className="gw-small" style={{ color: "var(--text-secondary)" }}>
              {submissions.length} total
            </span>
          </div>
          <button type="button" className="gw-btn-secondary" onClick={handleRefresh} data-testid={ADMIN.refreshBtn}>
            Refresh
          </button>
        </div>
        <div className="gw-card-body flex flex-col gap-3">
          {submissions.length === 0 && (
            <p className="gw-body" style={{ color: "var(--text-secondary)" }}>No profiles yet.</p>
          )}
          {submissions.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSelected(s)}
              data-testid={`${ADMIN.submissionRowPrefix}${s.id}`}
              className="flex flex-col gap-1.5 p-4 text-left"
              style={{
                border: "1px solid var(--border-card)",
                borderRadius: 12,
                background: "var(--page-secondary)",
                cursor: "pointer",
              }}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="gw-body" style={{ color: "var(--text-primary)", fontWeight: 600 }}>
                  {humanizeKey(s.primary_segment) || "—"}
                </span>
                <span className="gw-caption" style={{ color: "var(--text-secondary)" }}>
                  {s.submitted_at ? new Date(s.submitted_at).toLocaleDateString() : ""}
                </span>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <span className="gw-caption" style={{ color: "var(--text-secondary)" }}>
                  Account value: {s.account_value_score}
                </span>
                <span className="gw-caption" style={{ color: "var(--text-secondary)" }}>
                  Est. cost: {humanizeKey(s.estimated_systems_cost_band)}
                </span>
                <span className="gw-caption" style={{ color: "var(--text-secondary)" }}>
                  Action: {humanizeKey(s.recommended_sales_action)}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
